import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Lloyd Funding's privacy policy — how we collect, use, and protect your personal information."
        canonical="/privacy-policy"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Privacy <span className="text-blue-700">Policy</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your information.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Last updated: February 2026
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#FFFFFF"
            />
          </svg>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="prose prose-slate prose-lg max-w-none">
            <p className="text-slate-600 leading-relaxed">
              This Privacy Policy describes the information Lloyd Funding Group
              Inc DBA Lloyd Funding ("Lloyd Funding," "we," "us," or "our")
              collects about you ("User," "you," or "your"), how we use and
              share that information, and the privacy choices we offer. This
              policy applies to information we collect when you register for our
              services, access or use our website, mobile applications,
              products, and services (collectively, the "Services"), or when you
              otherwise interact with us.
            </p>

            <p className="text-slate-600 leading-relaxed">
              By accessing our website and using our Services, you agree to and
              are bound by this Privacy Policy. If you do not agree to this
              Privacy Policy, you may not access or otherwise use our website or
              Services.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              1. Information We Collect
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We may collect the following personal information about you:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li>Funding amount requested</li>
              <li>Time in business</li>
              <li>Annual and monthly revenue</li>
              <li>Credit score range</li>
              <li>Business legal name and structure</li>
              <li>Full name of business owners</li>
              <li>Email address</li>
              <li>Telephone number</li>
              <li>Business address</li>
              <li>Bank statements and financial documents</li>
              <li>Tax identification numbers</li>
            </ul>

            <h3 className="mt-8 text-xl font-semibold text-slate-800">
              Information Collected Automatically
            </h3>
            <p className="text-slate-600 leading-relaxed">
              We may also collect the following information automatically:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li>
                <strong>Cookies and Similar Technologies:</strong> We use
                cookies and similar technologies to collect information. Cookies
                are small data files stored on your device that help us improve
                our Services, retain your preferences, and analyze site traffic.
              </li>
              <li>
                <strong>Technical Information:</strong> We may collect your
                browser type, IP address, device information, pages visited, and
                time spent on our site. This information helps us improve our
                website design and functionality.
              </li>
              <li>
                <strong>Web Beacons:</strong> We may use web beacons (electronic
                images) in our Services or emails to deliver cookies, count
                visits, and understand usage patterns.
              </li>
            </ul>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              2. How We Use Your Information
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We may use the information you provide to:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li>Process and evaluate your funding application</li>
              <li>Connect you with appropriate lending partners</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>
                Offer you other products, programs, or services that may
                interest you
              </li>
              <li>Improve and personalize our Services</li>
              <li>Pre-fill form fields for your convenience</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Prevent fraud and enhance security</li>
            </ul>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              3. Information Sharing
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We may share your information with:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li>
                <strong>Lending Partners:</strong> We share your application
                information with our network of lending partners to help you
                find suitable funding options.
              </li>
              <li>
                <strong>Service Providers:</strong> We may share information
                with third-party service providers who assist us in operating
                our business, such as payment processors and email services.
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose information
                when required by law, court order, or government regulation.
              </li>
              <li>
                <strong>Business Transfers:</strong> In the event of a merger,
                acquisition, or sale of assets, your information may be
                transferred to the acquiring entity.
              </li>
            </ul>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              4. Data Security
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We take reasonable measures to protect your personal information
              from loss, theft, misuse, and unauthorized access, disclosure,
              alteration, and destruction. These measures include firewall
              protection, SSL encryption, and secure authentication procedures.
              However, no system is perfectly secure, and you use our Services
              and provide us with your personal information at your own risk.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              5. Email and SMS Communications
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We may send you communications via email and text message (SMS)
              regarding your application status, funding options, and
              promotional offers. By providing your telephone number and using
              our Services, you consent to receive automated and manual text
              messages from Lloyd Funding at the phone number you provided.
              Message frequency varies. Standard message and data rates may
              apply.
            </p>
            <p className="text-slate-600 leading-relaxed">
              You may opt out of promotional emails by following the unsubscribe
              instructions in those emails. You may opt out of text messages at
              any time by replying STOP to any message you receive from us.
              After opting out, you may receive one final confirmation message.
              For help, reply HELP or contact us at{" "}
              <a
                href="mailto:submissions@lloydfunding.com"
                className="text-blue-600 hover:text-blue-700"
              >
                submissions@lloydfunding.com
              </a>
              .
            </p>
            <p className="text-slate-600 leading-relaxed">
              Please note that opting out of promotional communications will not
              affect service-related communications, such as application updates
              and important notices related to your active funding process.
            </p>
            <p className="text-slate-600 leading-relaxed">
              No mobile information will be shared with third parties/affiliates
              for marketing/promotional purposes. All the above categories
              exclude text messaging originator opt-in data and consent; this
              information will not be shared with any third parties.
            </p>
            <p className="text-slate-600 leading-relaxed">
              View our{" "}
              <Link
                to="/terms-of-service"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Terms of Service
              </Link>{" "}
              for additional information about your use of our Services.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              6. Your Rights and Choices
            </h2>
            <p className="text-slate-600 leading-relaxed">
              You have the right to:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li>
                Access and review the personal information we hold about you
              </li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at{" "}
              <a
                href="mailto:submissions@lloydfunding.com"
                className="text-blue-600 hover:text-blue-700"
              >
                submissions@lloydfunding.com
              </a>
              .
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              7. Data Retention
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We retain your personal information for as long as necessary to
              fulfill the purposes for which it was collected, comply with legal
              obligations, resolve disputes, and enforce our agreements. Even
              after you request deletion, we may retain archived copies of your
              information for a period consistent with applicable law.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              8. Third-Party Links
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Our website may contain links to third-party websites. We are not
              responsible for the privacy practices of these websites. We
              encourage you to review the privacy policies of any third-party
              sites you visit.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              9. Children's Privacy
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Our Services are not intended for individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              you believe we have collected information from a child, please
              contact us immediately.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              10. Changes to This Privacy Policy
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify this Privacy Policy at any time.
              Changes will be effective immediately upon posting to our website.
              We encourage you to review this Privacy Policy periodically. Your
              continued use of our Services after any changes constitutes your
              acceptance of the updated Privacy Policy.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              11. Contact Us
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us at:
            </p>
            <div className="mt-4 rounded-xl bg-slate-50 p-6">
              <p className="font-semibold text-slate-800">
                Lloyd Funding Group Inc DBA Lloyd Funding
              </p>
              <p className="mt-2 text-slate-600">
                Email:{" "}
                <a
                  href="mailto:submissions@lloydfunding.com"
                  className="text-blue-600 hover:text-blue-700"
                >
                  submissions@lloydfunding.com
                </a>
              </p>
              <p className="text-slate-600">Phone: (786) 321-9346</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-slate-800 sm:text-3xl">
              Ready to get started?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Apply for funding today and get a decision within hours.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contact#application-form"
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 transition"
              >
                Apply Now
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-blue-600 ring-1 ring-blue-600 hover:bg-blue-50 transition"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
